import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { MfaReauthModal } from "@/Components/MfaReauthModal";
import { sentryLog } from "@/utils/sentry-utils";
import { setMfaReauthCallback } from "@/utils/init";
import { api } from "@/lib/api-client";
import { addToast } from "@heroui/react";
import { jwtDecode } from "jwt-decode";

type MfaReauthContextType = {
  showReauthModal: () => void;
  hideReauthModal: () => void;
};

const MfaReauthContext = createContext<MfaReauthContextType | null>(null);
let pendingPreAuthPromise: Promise<void> | null = null;
const PREAUTH_REFRESH_BUFFER_MS = 30_000;

type DecodedPreAuthToken = {
  exp?: number;
};

function parsePreAuthExpiry(preAuthToken: string): number | null {
  try {
    const decodedToken = jwtDecode<DecodedPreAuthToken>(preAuthToken);
    if (
      typeof decodedToken.exp !== "number" ||
      Number.isNaN(decodedToken.exp)
    ) {
      return null;
    }

    return decodedToken.exp * 1000;
  } catch {
    return null;
  }
}

export function MfaReauthProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [preAuthToken, setPreAuthToken] = useState<string | null>(null);
  const [preAuthExpiresAt, setPreAuthExpiresAt] = useState<number | null>(null);
  const [isFetchingPreAuth, setIsFetchingPreAuth] = useState(false);
  const [hasTotp, setHasTotp] = useState(false);
  const isOpenRef = useRef(false);
  const isFetchingPreAuthRef = useRef(false);

  const { mutateAsync: getReauthToken } = api.mfa.useAuthReauth();

  const resetModalState = useCallback(() => {
    isOpenRef.current = false;
    setIsOpen(false);
    setPreAuthToken(null);
    setPreAuthExpiresAt(null);
    setHasTotp(false);
  }, []);

  const fetchPreAuthToken = useCallback(async () => {
    if (pendingPreAuthPromise) {
      await pendingPreAuthPromise;
      return;
    }

    isFetchingPreAuthRef.current = true;
    setIsFetchingPreAuth(true);

    pendingPreAuthPromise = (async () => {
      try {
        const result = await getReauthToken();
        setPreAuthToken(result.preAuthToken);
        setPreAuthExpiresAt(parsePreAuthExpiry(result.preAuthToken));
        setHasTotp(result.factors.totp);
        sentryLog.info("Pre-auth token obtained for re-authentication");
      } catch (error) {
        sentryLog.error(
          "Failed to obtain pre-auth token for re-authentication",
          { error },
        );
        addToast({
          title: "Error",
          description:
            "Failed to initiate re-authentication. Please log in again.",
          color: "danger",
        });
        resetModalState();
        localStorage.removeItem("usr_token");
        localStorage.removeItem("usr_refresh_token");
        window.location.reload();
      } finally {
        isFetchingPreAuthRef.current = false;
        setIsFetchingPreAuth(false);
        pendingPreAuthPromise = null;
      }
    })();

    await pendingPreAuthPromise;
  }, [getReauthToken, resetModalState]);

  const showReauthModal = useCallback(async () => {
    if (!isOpenRef.current) {
      sentryLog.info("MFA re-auth modal triggered");
      isOpenRef.current = true;
      setIsOpen(true);
    }

    await fetchPreAuthToken();
  }, [fetchPreAuthToken]);

  useEffect(() => {
    setMfaReauthCallback(() => {
      void showReauthModal();
    });

    return () => {
      setMfaReauthCallback(null);
      pendingPreAuthPromise = null;
    };
  }, [showReauthModal]);

  useEffect(() => {
    if (!isOpen || preAuthExpiresAt == null) {
      return;
    }

    const msUntilRefresh =
      preAuthExpiresAt - Date.now() - PREAUTH_REFRESH_BUFFER_MS;

    if (msUntilRefresh <= 0) {
      void fetchPreAuthToken();
      return;
    }

    const timeoutId = window.setTimeout(() => {
      void fetchPreAuthToken();
    }, msUntilRefresh);

    return () => window.clearTimeout(timeoutId);
  }, [fetchPreAuthToken, isOpen, preAuthExpiresAt]);

  const hideReauthModal = () => {
    resetModalState();
  };

  const handleSuccess = (accessToken: string) => {
    localStorage.setItem("usr_token", accessToken);
    resetModalState();
    sentryLog.info("MFA re-auth successful, reloading page");
    window.location.reload();
  };

  const handleCancel = () => {
    sentryLog.info("MFA re-auth cancelled, logging out");
    localStorage.removeItem("usr_token");
    localStorage.removeItem("usr_refresh_token");
    isFetchingPreAuthRef.current = false;
    setIsFetchingPreAuth(false);
    resetModalState();
    pendingPreAuthPromise = null;
    window.location.reload();
  };

  return (
    <MfaReauthContext.Provider value={{ showReauthModal, hideReauthModal }}>
      {children}
      <MfaReauthModal
        isOpen={isOpen}
        preAuthToken={preAuthToken}
        preAuthExpiresAt={preAuthExpiresAt}
        isFetchingPreAuth={isFetchingPreAuth}
        hasTotp={hasTotp}
        onRefreshPreAuthToken={fetchPreAuthToken}
        onSuccess={handleSuccess}
        onCancel={handleCancel}
      />
    </MfaReauthContext.Provider>
  );
}

export function useMfaReauth() {
  const context = useContext(MfaReauthContext);
  if (!context) {
    throw new Error("useMfaReauth must be used within MfaReauthProvider");
  }
  return context;
}
